/**
 * useLevelColoring.js
 *
 * Pure-function composable for the multi-level condition coloring pipeline (AVA-348).
 *
 * Provides bucket resolution, severity ranking, legend-with-counts, and
 * worst-child-wins element paint mapping across three coloring levels:
 *   Inspection — reuses existing predefined filter color buckets (handled upstream in T6)
 *   Master     — matches each master tag's cached worst-of metrics to a color bucket
 *   CML        — per-element worst-child-wins across CML records sharing a ForgeElementDbId
 *
 * Consumed by:
 *   T10 — buildColorOptions (legend counts per level)
 *   T11 — worstChildWinsByDbId (3D element paint at CML level)
 *   T13 — marker coloring (places colored markers per CML point)
 *
 * ⚠ Severity is ranked by RAW METRIC VALUE (or CmlConditionRating enum rank),
 *   NOT by bucket position in the color-options array. Array order is inconsistent
 *   across metric types. Getting this backwards paints failing equipment "good".
 */

import { bucketOf, statusBucketOf } from '@/utils/colorBuckets';
import { LEVEL, METRIC_KEY, CONDITION_RATING, NO_DATA_COLOR } from './cmlColoringConstants';

/** The three supported coloring levels, in display order. */
export const LEVELS = [LEVEL.INSPECTION, LEVEL.MASTER, LEVEL.CML];

/** Viewer-responsiveness cap for projected WebGL glyphs and DOM labels. */
export const MARKER_BUDGET = 300;

/**
 * Status metric buckets (legend display order).
 * Colors mirror forgeService._getCmlConditionColor.
 * NOTE: Severity is NOT array order — see STATUS_RANK below.
 *
 * @type {Array<{ key: string, title: string, judgCor: string }>}
 */
export const STATUS_OPTIONS = [
  { key: CONDITION_RATING.ACCEPTABLE,      title: 'Acceptable',     judgCor: '#4CAF50' },
  { key: CONDITION_RATING.MONITOR,         title: 'Monitor',        judgCor: '#FBC02D' },
  { key: CONDITION_RATING.RE_INSPECT,      title: 'ReInspect',      judgCor: '#FF9800' },
  { key: CONDITION_RATING.FFS_REQUIRED,    title: 'FfsRequired',    judgCor: '#9C27B0' },
  { key: CONDITION_RATING.REPAIR_REQUIRED, title: 'RepairRequired', judgCor: '#F44336' },
  { key: CONDITION_RATING.NO_DATA,         title: 'No data',        judgCor: NO_DATA_COLOR },
];

/**
 * Severity rank for Status (CmlConditionRating order).
 * NoData/unknown rank lowest so a no-data CML never outranks a real condition
 * in worst-child-wins comparisons.
 * @type {Object.<string, number>}
 */
const STATUS_RANK = {
  [CONDITION_RATING.ACCEPTABLE]: 0,
  [CONDITION_RATING.MONITOR]: 1,
  [CONDITION_RATING.RE_INSPECT]: 2,
  [CONDITION_RATING.FFS_REQUIRED]: 3,
  [CONDITION_RATING.REPAIR_REQUIRED]: 4,
};

/**
 * Metric key → field on a master/cml coloring row + comparison direction.
 *   dir 'max' = higher value is worse (Corrosion Rate: more corrosion = worse).
 *   dir 'min' = lower value is worse (Remaining Life, Thickness: less = worse).
 * @type {Object.<string, { field: string, dir: 'max'|'min' }>}
 */
const METRIC = {
  [METRIC_KEY.CORR_RATE]:  { field: 'worstCR',      dir: 'max' },
  [METRIC_KEY.REM_LIFE]:   { field: 'minRL',        dir: 'min' },
  [METRIC_KEY.THICKNESS]:  { field: 'currentMinWT', dir: 'min' },
};

/**
 * Stable bucket id for BOTH legend display and selected-filter comparison.
 * Numeric predefined buckets → `${code}-${codeOrdr}` (matches drawer's existing selected ids).
 * Status buckets → the key string.
 *
 * @param {object|null} option
 * @returns {string|null}
 */
export function bucketId(option) {
  if (!option) return null;
  if (option.key != null) return option.key;
  if (option.code != null && option.codeOrdr != null) return `${option.code}-${option.codeOrdr}`;
  return option.id ?? option.title ?? null;
}

/**
 * True when an option is the "no data / N/A" bucket for its metric.
 *   Status   → the NO_DATA-keyed bucket.
 *   Numeric  → the bucket with no comparison operators (matches only a missing value;
 *              see colorBuckets.matchesBucket — judgMkr1/judgMkr2 both null).
 * Used to default-exclude no-data items from the level auto-selection.
 *
 * @param {object|null} option
 * @returns {boolean}
 */
export function isNoDataOption(option) {
  if (!option) return false;
  if (option.key != null) return option.key === CONDITION_RATING.NO_DATA;
  return option.judgMkr1 == null && option.judgMkr2 == null;
}

/**
 * The raw metric value for an entity under the active metric key.
 *
 * @param {'CorrRate'|'RemLife'|'Thickness'|'Status'} metricKey
 * @param {{ worstCR?: number|null, minRL?: number|null, currentMinWT?: number|null, conditionRating?: string|null }} entity
 * @returns {number|string|null}
 */
export function valueForLevel(metricKey, entity) {
  if (metricKey === METRIC_KEY.STATUS) return entity.conditionRating ?? null;
  const m = METRIC[metricKey];
  if (!m) throw new Error(`useLevelColoring.valueForLevel: unknown metricKey "${metricKey}"`);
  return entity[m.field] ?? null;
}

/**
 * Resolve the bucket (color option) an entity falls into for the active metric.
 *
 * @param {{ key: 'CorrRate'|'RemLife'|'Thickness'|'Status', colorOptions?: Array }} metric
 * @param {object} entity - A master or CML coloring row.
 * @returns {object|null} The matched option, or null when no bucket matches.
 */
export function bucketForEntity(metric, entity) {
  if (metric.key === METRIC_KEY.STATUS) return statusBucketOf(entity.conditionRating, STATUS_OPTIONS);
  return bucketOf(valueForLevel(metric.key, entity), metric.colorOptions);
}

/**
 * Severity scalar — higher = worse.
 * null / N/A values → -Infinity (lowest) so they never displace a real condition.
 * Direction-aware for numeric metrics (CorrRate: higher CR is worse; RemLife/Thickness: lower is worse).
 *
 * @param {{ key: 'CorrRate'|'RemLife'|'Thickness'|'Status', colorOptions?: Array }} metric
 * @param {object} entity - A master or CML coloring row.
 * @returns {number}
 */
export function severityOf(metric, entity) {
  if (metric.key === METRIC_KEY.STATUS) {
    const r = entity.conditionRating;
    return (r != null && r in STATUS_RANK) ? STATUS_RANK[r] : -Infinity;
  }
  const v = valueForLevel(metric.key, entity);
  if (v == null) return -Infinity;
  // v is numeric per the DTO contract; null is already excluded above (so -v is never NaN).
  return METRIC[metric.key].dir === 'min' ? -v : v;
}

/**
 * Build legend rows with entity counts in one O(n) pass.
 * Returns a NEW array of options with `id` and `count` added — originals are not mutated.
 * Order follows the source options' display order.
 *
 * @param {{ key: 'CorrRate'|'RemLife'|'Thickness'|'Status', colorOptions?: Array }} metric
 * @param {Array} entities - Master or CML coloring rows.
 * @returns {Array<object>} Options enriched with `{ id, count }`.
 */
export function buildColorOptions(metric, entities) {
  const opts = metric.key === METRIC_KEY.STATUS ? STATUS_OPTIONS : (metric.colorOptions || []);
  const counts = new Map(opts.map(o => [bucketId(o), 0]));
  (entities || []).forEach(e => {
    const id = bucketId(bucketForEntity(metric, e));
    if (id != null && counts.has(id)) counts.set(id, counts.get(id) + 1);
  });
  return opts.map(o => { const id = bucketId(o); return { ...o, id, count: counts.get(id) || 0 }; });
}

/**
 * CML element paint: among all CMLs sharing a ForgeElementDbId, keep those whose
 * bucket is in the selected set, then the one with the highest severity (worst by
 * raw metric value — NOT array position) wins the element's color.
 *
 * CML LEVEL ONLY. Masters don't share a dbId and carry their own cached worst-of.
 * CMLs with null forgeElementDbId (placed-only, no geometry) are skipped.
 *
 * @param {Array} cmlRows - CML coloring rows from the API.
 * @param {{ key: 'CorrRate'|'RemLife'|'Thickness'|'Status', colorOptions?: Array }} metric
 * @param {string[]|null} selectedBucketIds - When non-empty, only CMLs in these buckets compete.
 *   Pass null or [] to include all buckets (no filter).
 * @returns {Map<number, { color: string, sev: number }>} dbId → winning color + severity scalar.
 */
export function worstChildWinsByDbId(cmlRows, metric, selectedBucketIds) {
  const selected = selectedBucketIds && selectedBucketIds.length ? new Set(selectedBucketIds) : null;
  const map = new Map(); // dbId → { color, sev }

  (cmlRows || []).forEach(c => {
    if (c.forgeElementDbId == null) return;               // placed-only — no 3D geometry (§9)
    const bucket = bucketForEntity(metric, c);
    if (!bucket) return;                                   // no matching bucket (value out of range)
    if (selected && !selected.has(bucketId(bucket))) return; // filter-to-selected before worst-wins
    const sev = severityOf(metric, c);
    const prev = map.get(c.forgeElementDbId);
    if (!prev || sev > prev.sev) map.set(c.forgeElementDbId, { color: bucket.judgCor, sev });
  });

  return map;
}

/**
 * Worst-first marker budget. Ranks CMLs by CONDITION severity (worst first) and
 * keeps at most MARKER_BUDGET, so no marker entry point projects thousands of
 * WebGL glyphs and DOM labels (mirrors the cap InspectionFiltersDrawer applies).
 * Worst-first is only correct over the FULL CML set — callers must pass the
 * complete list (the loader now batch-loads all pages).
 *
 * @param {Array} cmls - full CML list (monitoring records w/ `conditionRating`).
 * @returns {{ cmls: Array, total: number, shown: number, capped: boolean }}
 */
export function budgetCmlsForMarkers(cmls) {
  const list = Array.isArray(cmls) ? cmls : [];
  if (list.length <= MARKER_BUDGET) {
    return { cmls: list, total: list.length, shown: list.length, capped: false };
  }
  const statusMetric = { key: METRIC_KEY.STATUS };
  const sorted = [...list].sort((a, b) => severityOf(statusMetric, b) - severityOf(statusMetric, a));
  const subset = sorted.slice(0, MARKER_BUDGET);
  return { cmls: subset, total: list.length, shown: subset.length, capped: true };
}
